import useBranchStore from "@/stores/branch.store";

// The seven doctypes whose inserts are branch-scoped on the backend
// (pet_app/utils/branch.py SCOPED_DOCTYPES). A new record of any of these that
// is created while the user is unrestricted AND more than one Branch exists
// throws MandatoryError unless the client supplies `branch`.
//
// Pet Boarding is deliberately NOT here — boarding is global (one facility
// serves both clinics) and its branch comes from a setting, not the operator.
export const SCOPED_BRANCH_DOCTYPES = new Set<string>([
  "Vet Visit",
  "Appointment",
  "Vet Case Sheet",
  "Pet Queue Ticket",
  "PetCareService",
  "Pet Procedure",
  "Sales Invoice",
  /**
   * Contract §1.3. An operator restricted to one clinic gets `branch` stamped automatically;
   * an UNRESTRICTED one (back-office, admin) must send it or the create raises
   * `MandatoryError: "Select a branch for this Preventive Care Record…"`.
   *
   * That is exactly what this set means, so the existing guard supplies the picker rather than
   * the create dialog growing a bespoke branch field. This site has two branches
   * (`main`, `hotel`), so the unrestricted case is live rather than theoretical.
   */
  "Preventive Care Record",
]);

/**
 * Core resolver: the `branch` argument to send for a scoped create, opening the
 * modal when the backend cannot infer it and no choice has been made yet.
 *
 * Rules (mirrors the backend resolution order):
 *  - Not a scoped doctype   → `{}` (send nothing).
 *  - `must_select` is false → `{}`; the backend stamps the user's own / the sole
 *                             branch. NEVER send null/"" — that is the same as
 *                             sending nothing and re-throws.
 *  - `must_select` is true  → the session choice, opening the picker on first use
 *                             and caching it so nothing re-prompts.
 *
 * The result is `{}` or `{ branch }` — spread it into a client doc OR a backend
 * whitelisted-method payload, so a create that is built server-side (start_visit,
 * create_queue_ticket, …) is covered exactly like a client-built one.
 *
 * Rejects with `BranchSelectionCancelledError` if the user cancels, so the caller
 * aborts the create without sending a request.
 */
export async function resolveBranchArg(
  doctype: string,
): Promise<{ branch: string } | Record<string, never>> {
  if (!SCOPED_BRANCH_DOCTYPES.has(doctype))
    return {};

  const store = useBranchStore();

  // Boot fetches this eagerly, but guard against a create that races startup.
  if (!store.loaded)
    await store.load();

  if (!store.mustSelect)
    return {};

  const branch = await store.chooseBranch();

  return { branch };
}

/**
 * Merge the required `branch` field into a client-built document/payload for a
 * scoped insert. Thin wrapper over `resolveBranchArg` that respects a branch the
 * doc already carries (e.g. a return invoice inheriting its source's branch).
 *
 * Only meaningful on INSERT — the backend hook is `before_insert`. Callers must
 * not run this on the update/PUT path.
 */
export async function applyBranchToDoc<T extends Record<string, any>>(
  doctype: string,
  doc: T,
): Promise<T> {
  if (doc && (doc as Record<string, any>).branch)
    return doc;

  const arg = await resolveBranchArg(doctype);

  return "branch" in arg ? { ...doc, ...arg } : doc;
}
